import React from 'react';

import { Box } from '../../../../../Box';
import { Typography } from '../../../../../Typography';
import { Avatar } from '../../../../../Avatar';
import { EllipsisText } from '../../../../../EllipsisText';

import { useStyles } from './UserItem.styles';

interface UserItemProps {
  data: {
    id: string;
    first_name?: string;
    second_name?: string;
    middle_name?: string;
    name?: string;
    logo?: string;
  };
  inputValue: string;
  handleSelectValue: (value: {
    name: string;
    id: string;
    type: 'unit' | 'user';
    logo?: string;
  }) => void;
}

export const UserItem = ({
  data,
  inputValue,
  handleSelectValue,
}: UserItemProps) => {
  const { classes } = useStyles();

  const {
    first_name: firstName,
    middle_name: middleName,
    second_name: secondName,
    logo,
    id,
    name,
  } = data;

  const isUnit = !!name;

  const highlightLetters = (text: string, lettersToHighlight: string) => {
    if (!lettersToHighlight.length) {
      return (
        <Typography
          variant="body2"
          color="text.light.primary"
          component="span"
          noWrap
        >
          <EllipsisText wordBreak="break-all" rows={1}>
            {text}
          </EllipsisText>
        </Typography>
      );
    }

    const words = text.split(' ');
    const formattedWords = words.map((word) => {
      const safeLetters = lettersToHighlight.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&',
      );

      const partToHighlight = word
        .slice(0, lettersToHighlight.length)
        .split(new RegExp(`(${safeLetters})`, 'gi'));
      const restPart = word.slice(lettersToHighlight.length, word.length);

      return (
        <React.Fragment key={word}>
          {[...partToHighlight, restPart].map((part: string, index, arr) => {
            const currentPart =
              arr.length === index + 1 ? `${part}${'\u00A0'}` : part;

            return part.toLowerCase() === lettersToHighlight.toLowerCase() ? (
              <Typography
                variant="body2"
                color="text.light.primary"
                component="span"
                noWrap
              >
                {currentPart}
              </Typography>
            ) : (
              currentPart
            );
          })}
        </React.Fragment>
      );
    });

    return (
      <Typography
        variant="body2"
        color="text.light.tertirary"
        component="span"
        noWrap
      >
        <EllipsisText wordBreak="break-all" rows={1}>
          {formattedWords}
        </EllipsisText>
      </Typography>
    );
  };

  const onSelect = (
    itemName: string,
    type: 'unit' | 'user',
    itemLogo?: string,
  ) => {
    handleSelectValue({
      name: itemName,
      id,
      type,
      logo: itemLogo,
    });
  };

  const stringAvatar = () =>
    isUnit
      ? `${name?.charAt(0)}`
      : `${firstName?.charAt(0)}${secondName?.charAt(0)}`;

  return (
    <Box
      px="16"
      py="12"
      display="flex"
      alignItems="center"
      gap="8"
      id={id}
      cursor="pointer"
      onClick={() =>
        onSelect(
          isUnit ? name : `${secondName} ${firstName} ${middleName}`,
          isUnit ? 'unit' : 'user',
          logo,
        )
      }
      className={classes.container}
    >
      <Avatar size="extraSmall" src={logo}>
        {stringAvatar()}
      </Avatar>

      <Box
        display="flex"
        alignItems="center"
        flexWrap="nowrap"
        width="calc(100% - 30px)"
      >
        {highlightLetters(
          isUnit ? name : `${secondName} ${firstName} ${middleName}`,
          inputValue,
        )}
      </Box>
    </Box>
  );
};
